import type { Host, HostState } from "../../api/types";
import { Spinner } from "../../components/AsyncStates";
import { providerLabel } from "./hostFormatting";
import "./provisioning-progress.css";

/**
 * The provisioning-as-progress sequence (Host/Deploy UX Phase 3, design
 * review): before this, an operator watched provisioning as "submit a modal
 * -> a toast -> a static table row with a status pill you must manually
 * refetch" — this component is what replaces the pill-only read with an
 * honest, live sequence. It covers exactly the SUBSET of `HostState`
 * (types.ts) that is "something is in flight or just went wrong":
 * `provisioning` and `enrolling` are genuinely in-flight steps of the SAME
 * sequence; `provision_failed` is the dead-end that sequence can also land
 * on. Every OTHER HostState (`online`, `offline`, `decommissioning`,
 * `deleted`) means there's nothing in progress to show — `relevantState`
 * returns `undefined` for those and `ProvisioningProgress` renders nothing
 * (A-10: no progress UI where the state machine says there's no progress
 * happening). A host with no provisioning facet (BYO — `provisioning: null`)
 * is unaffected the same way, for the same reason: its state derives from
 * the live agent, not a Comebye-run provisioning request.
 */
type ProgressState = Extract<HostState, "provisioning" | "enrolling" | "provision_failed">;

/** The two REAL in-flight stages a sequence can render as steps — mirrors
 * `hostsvc.deriveState`'s precedence exactly (see `HostState`'s doc comment
 * in types.ts): a Comebye-provisioned host is "provisioning" until the
 * provider reports it up, then "enrolling" ("running ≠ enrolled") until
 * Kelpie's first check-in makes it "online". `provision_failed` never
 * advances past the first step (see `stepStatus`), so it isn't itself one of
 * the ordered stages here — it's a terminal outcome the sequence renders on
 * top of. */
const STAGE_ORDER: Array<Extract<HostState, "provisioning" | "enrolling">> = ["provisioning", "enrolling"];

const STAGE_LABEL: Record<(typeof STAGE_ORDER)[number], string> = {
  provisioning: "Provisioning",
  enrolling: "Enrolling",
};

function relevantState(state: HostState): ProgressState | undefined {
  return state === "provisioning" || state === "enrolling" || state === "provision_failed" ? state : undefined;
}

/**
 * The one honest, present-tense line an operator actually needs to read for
 * the host's CURRENT stage — no fabricated ETA or percentage (A-10): "this
 * can take a minute" and "waiting for…" are honest about uncertainty rather
 * than inventing a countdown Comebye has no real number for. Exported so the
 * Hosts list (compact) and the Host detail page (full sequence) render the
 * IDENTICAL wording instead of two hand-written copies that could drift
 * apart (A-5).
 */
export function provisioningStatusLine(host: Host): string | undefined {
  const state = relevantState(host.state);
  if (!state || !host.provisioning) return undefined;
  const { provider, region } = host.provisioning;
  switch (state) {
    case "provisioning":
      return `Booting at ${providerLabel(provider)} (${region})… this can take a minute.`;
    case "enrolling":
      return `Up at ${providerLabel(provider)} — waiting for its Kelpie agent to check in for the first time.`;
    case "provision_failed":
      return "Provisioning failed — retry or destroy from Instances.";
  }
}

type DotStatus = "done" | "active" | "pending" | "error";

function dotStatus(stage: (typeof STAGE_ORDER)[number], state: ProgressState): DotStatus {
  if (state === "provision_failed") {
    // The sequence never advances past its first step on a failure — mirrors
    // computeHostState/hostsvc.deriveState: a failed instance never reaches
    // "enrolling" (there was never a machine to enroll an agent onto).
    return stage === "provisioning" ? "error" : "pending";
  }
  const stageIdx = STAGE_ORDER.indexOf(stage);
  const currentIdx = STAGE_ORDER.indexOf(state);
  if (stageIdx < currentIdx) return "done";
  if (stageIdx === currentIdx) return "active";
  return "pending";
}

interface ProvisioningProgressProps {
  host: Host;
  /**
   * `"list"` renders a compact inline strip for a Hosts table row (dots +
   * one-line caption, no live region — the row already re-renders on every
   * poll, and a table full of individually-announcing live regions would be
   * noisy for a screen-reader user watching several hosts provision at
   * once). `"detail"` renders the fuller labeled step sequence for the Host
   * card, with the active step in a single `role="status"` region (via
   * `Spinner`) — mirrors `HostDetailPage`'s pre-existing progress paragraph.
   *
   * `provision_failed` only ever renders the `"list"` variant: the Host
   * detail page's own remediation `Banner` (see HostDetailPage.tsx) already
   * covers "what failed + the actionable next step" for that state, so this
   * component stays out of its way there rather than rendering a second,
   * competing explanation.
   */
  variant: "list" | "detail";
}

/**
 * Renders nothing when the host has no provisioning facet (BYO) or isn't in
 * one of the three states this sequence covers (A-10 — see the file doc
 * comment above).
 */
export function ProvisioningProgress({ host, variant }: ProvisioningProgressProps) {
  const state = relevantState(host.state);
  if (!state || !host.provisioning) return null;
  if (state === "provision_failed" && variant === "detail") return null;

  const line = provisioningStatusLine(host);

  if (variant === "list") {
    return (
      <span className="provisioning-progress provisioning-progress--list">
        <span className="provisioning-progress__dots" aria-hidden="true">
          {STAGE_ORDER.map((stage) => (
            <span
              key={stage}
              className={`provisioning-progress__dot provisioning-progress__dot--${dotStatus(stage, state)}`}
            />
          ))}
        </span>
        <span className="provisioning-progress__caption">{line}</span>
      </span>
    );
  }

  return (
    <div className="provisioning-progress provisioning-progress--detail">
      <ol className="provisioning-progress__steps" aria-hidden="true">
        {STAGE_ORDER.map((stage) => (
          <li
            key={stage}
            className={`provisioning-progress__step provisioning-progress__step--${dotStatus(stage, state)}`}
          >
            {STAGE_LABEL[stage]}
          </li>
        ))}
      </ol>
      {/* Spinner already carries its own role="status" + accessible label
          (see HostDetailPage.tsx's identical note) — this paragraph is NOT
          itself a second live region, so the two never double-announce. */}
      <p className="host-detail__progress">
        <Spinner />
        {line}
      </p>
    </div>
  );
}
